import type { DefineComponent, ExtractPropTypes, SetupContext } from 'vue';
import type { blockRenderProps } from '#layers/block-engine/components/Block/BlockRenderShared';
import { h, inject, markRaw, onScopeDispose, ref, resolveComponent, shallowRef, toRaw, watch } from 'vue';
import BlockErrorBoundary from '#layers/block-engine/components/Block/BlockErrorBoundary.vue';
import BlockRenderComponent from '#layers/block-engine/components/Block/BlockRenderComponent';
import {
	blockPrivateRootContextFallbackKey,
	blockRootContextApiFallbackKey,
	shouldRegisterBlockRootContext,
} from '#layers/block-engine/components/Block/BlockRenderShared';
import { useBlockResolverStore } from '#layers/block-engine/stores/block-resolver';
import { resolveBuiltinBlock } from '#layers/block-engine/utils/block/resolve';

interface WrappedBlockRenderProps {
	/**
	 * Options to pass to `<BlockRender>`
	 */
	blockRender?: Partial<Omit<ExtractPropTypes<typeof blockRenderProps>, 'block' | 'props'>>;
}

type BlockRenderWrapper = DefineComponent<WrappedBlockRenderProps>;

/**
 * Resolve builtin blocks without creating reactive component wrappers.
 */
function resolveWrappedBuiltinBlock(blockId: string) {
	const block = resolveBuiltinBlock(blockId);
	return block ? markRaw(toRaw(block)) : null;
}

/**
 * Wrapped block renderer preserving the source component's public type.
 */
export type WrappedBlockRenderer<TComponent> = TComponent & BlockRenderWrapper;

export function createWrappedBlockRender<TComponent>(comp: TComponent, block: string): WrappedBlockRenderer<TComponent>;
/** @deprecated Use the two-argument form `createWrappedBlockRender(comp, block)` */
export function createWrappedBlockRender<TComponent>(block: string): WrappedBlockRenderer<TComponent>;
export function createWrappedBlockRender<TComponent>(compOrBlock: TComponent | string, maybeBlock?: string) {
	const block = typeof compOrBlock === 'string' ? compOrBlock : maybeBlock!;
	return {
		props: ['blockRender'],
		inheritAttrs: false,
		setup: (props: WrappedBlockRenderProps, { attrs, slots }: SetupContext) => {
			const { onBlockRefresh } = useBlockResolverStore();
			const errorBlockName = ref(block);
			const blockIcon = ref<string | undefined>(undefined);
			const refreshKey = ref(0);
			// Wrapper-generated components almost always point at builtin blocks.
			// Resolve once here so common wrappers can jump directly to the sync
			// component renderer instead of nesting through BlockRender again.
			const resolvedBlock = shallowRef(resolveWrappedBuiltinBlock(block));

			const stopRefreshing = onBlockRefresh((refreshedBlockId) => {
				if (refreshedBlockId !== block) {
					return;
				}

				resolvedBlock.value = resolveWrappedBuiltinBlock(block);
				refreshKey.value++;
			});

			onScopeDispose(() => {
				stopRefreshing.off();
			});

			const rootContextApiFallback = inject(blockRootContextApiFallbackKey, null);
			const getPrivateRootContextFallback = inject(blockPrivateRootContextFallbackKey, null);

			function getPrivateRootContext() {
				return {
					...getPrivateRootContextFallback?.(),
					...props.blockRender?.privateContext,
				};
			}

			watch(resolvedBlock, (value) => {
				if (!value) {
					return;
				}

				errorBlockName.value = value.name;
				blockIcon.value = value.icon ?? undefined;
			}, {
				immediate: true,
			});

			const renderFastBlock = () => {
				if (!resolvedBlock.value) {
					// Keep the old BlockRender path as a safety net for unusual cases,
					// such as missing builtin registrations during development.
					return h(resolveComponent('BlockRender'), {
						...props.blockRender,
						block,
						props: attrs,
					}, slots);
				}

				// `attrs` already flows into the rendered block via the `props` prop
				// below, which BlockRenderComponent spreads onto the underlying
				// component. Spreading `...attrs` here as well would cause the same
				// attributes to be applied twice (once as vnode attrs on the
				// wrapper, once inside the child component's props), leading to
				// duplicated DOM attributes and duplicate event listeners.
				return h(BlockRenderComponent, {
					key: `${block}:${refreshKey.value}`,
					block: resolvedBlock.value,
					blockId: block,
					props: attrs,
					spacing: props.blockRender?.spacing,
					unwrapBlockDefinition: props.blockRender?.unwrapBlockDefinition,
					idOverride: props.blockRender?.idOverride,
					context: props.blockRender?.context,
					privateContext: getPrivateRootContext(),
					registerRootContext: shouldRegisterBlockRootContext({
						registerRootContext: props.blockRender?.registerRootContext,
						inheritedRootContextApi: rootContextApiFallback,
					}),
				}, slots);
			};

			return () => props.blockRender?.skipErrorBoundary
				? renderFastBlock()
				: h(BlockErrorBoundary, {
						name: errorBlockName.value,
						icon: blockIcon.value,
					}, renderFastBlock);
		},
	} as unknown as WrappedBlockRenderer<TComponent>;
}
